import { RuleValidationAction } from "./rules/triggers";
import {
	getDefaultNodeState,
	ImperativeValidationState,
	isDisconnectedFromValidationStructure,
	NodeValidationState
} from "./state";
import { IValidator } from "./validator";
import { ModelPath } from "./path";
import { ROOT_NODE_PATH } from "./builder";
import { ConversionError } from "client/Forms/converters";
import { InputValidationResultKind } from "./input";

export type SingleInputValidationParams = {
	action: Exclude<RuleValidationAction, "formSubmit" | "stepSubmit">;
	initiatorModelPath: ModelPath;
};

export type ExecuteValidationParams =
	| SingleInputValidationParams
	| {
		action: "stepSubmit";
		initiatorModelPaths: ReadonlySet<ModelPath>;
	}
	| {
		action: "formSubmit";
	};

export type ReadonlyImplicitValidatorsDictionary = ReadonlyMap<
	string,
	ReadonlyMap<string, IValidator<unknown>>
>;

export async function executeValidation<TModel>(
	model: TModel,
	rootValidator: IValidator<TModel>,
	params: ExecuteValidationParams,
	nodeStates: readonly NodeValidationState[],
	conversionErrorMap: ReadonlyMap<string, ConversionError>,
	imperativeValidationResults: ReadonlyMap<string, ImperativeValidationState>,
	signal?: AbortSignal
): Promise<readonly NodeValidationState[]> {
	nodeStates = await rootValidator.execute(
		{
			value: model,
			parentValue: undefined,
			stablePath: ROOT_NODE_PATH,
			modelPath: ModelPath.root
		},
		params,
		nodeStates,
		conversionErrorMap,
		signal
	);

	nodeStates = applyImperativeValidationResults(
		nodeStates,
		imperativeValidationResults
	);

	return nodeStates;
}

function applyImperativeValidationResults(
	nodeStates: readonly NodeValidationState[],
	imperativeValidation: ReadonlyMap<string, ImperativeValidationState>,
): readonly NodeValidationState[] {
	const resultNodeStates: NodeValidationState[] = [];
	const processedNodes = new Set<string>();

	for (let nodeState of nodeStates) {
		const imperativeState: ImperativeValidationState | undefined = imperativeValidation
			.get(nodeState.stablePath);

		processedNodes.add(nodeState.stablePath);

		nodeState = {
			...nodeState,
			controlValidation: imperativeState?.controlValidation,
			serverResult: imperativeState?.serverResult
				?? { kind: InputValidationResultKind.Valid }
		};

		resultNodeStates.push(nodeState);
	}

	for (const [stablePath, imperativeState] of imperativeValidation.entries()) {
		if (processedNodes.has(stablePath)) {
			continue;
		}

		processedNodes.add(stablePath);

		// If stable path originates from validation structure it should be processed by the first loop.
		// If it was not processed by the first loop it means the element matching the path is removed and we should skip it.
		if (!isDisconnectedFromValidationStructure(stablePath)) {
			continue;
		}

		const nodeState: NodeValidationState = {
			...getDefaultNodeState(
				stablePath,
				imperativeState.modelPath,
				0
			),
			controlValidation: imperativeState.controlValidation,
			serverResult: imperativeState.serverResult
				?? { kind: InputValidationResultKind.Valid }
		};

		resultNodeStates.push(nodeState);
	}

	return resultNodeStates;
}